import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, getToken } from "../api";

const TITLES = { QUOTE: "견적서 작성", CONTRACT: "계약서 작성", INVOICE: "인보이스 작성", PO: "발주서 작성" };
const DOC_SHORT = { QUOTE: "견적서", CONTRACT: "계약서", INVOICE: "인보이스", PO: "발주서" };
const TYPE_ROUTE = { QUOTE: "quotes", CONTRACT: "contracts", INVOICE: "invoices", PO: "orders" };
const NOTE_LABEL = { QUOTE: "특약 및 안내사항", CONTRACT: "계약 내용", INVOICE: "특약 및 안내사항", PO: "특약 및 안내사항" };
// 문서 종류별 상대/자사·품목·개요 라벨 (발주서는 역할이 반대)
const CLIENT_LABEL = { PO: "공급처" };      // 기본 "고객사"
const SUPPLIER_LABEL = { PO: "발주처" };    // 기본 "공급사"
const ITEMS_LABEL = { CONTRACT: "계약 항목", PO: "구매 품목" };  // 기본 "상세 내역"
const SERVICE_LABEL = { PO: "발주 개요" };  // 기본 "제공 서비스"
const clientLabel = (t) => CLIENT_LABEL[t] || "고객사";
const supplierLabel = (t) => SUPPLIER_LABEL[t] || "공급사";
const itemsLabel = (t) => ITEMS_LABEL[t] || "상세 내역";
const serviceLabel = (t) => SERVICE_LABEL[t] || "제공 서비스";
// 인보이스는 유효기한 대신 결제기한
const validityLabel = (t) => (t === "INVOICE" ? "결제기한" : "유효기한");
const validityDefault = (t) => (t === "INVOICE" ? "작성일로부터 30일" : "견적일로부터 30일");

// 공급자(I.META) 기본 정보 — 상호 · 사업자번호 등은 고정(수정 버튼으로만 변경), 업태 · 종목은 서류마다 바로 고칠 수 있다
const SUPPLIER_DEFAULT = {
  name: "아이닷메타(i.meta)",
  biz_reg_no: "394-61-00264",
  representative: "선성태",
  address: "전남 보성군 벌교읍 벌교연동길 40",
  tel: "010-9601-4161",
  email: "idotmeta@gmail.com",
  biz_condition: "정보통신업 / 제조업",
  biz_item: "전자기기 제조업 / 응용 소프트웨어 개발 및 공급업",
};

// 특약 및 안내사항 기본 문구 (한 줄에 하나씩, 번호로 표시됨)
const noteDefault = (docType) =>
  docType === "PO"
    ? [
        "결제방식 : 협의",
        "납기 : 협의",
        "본 발주 금액은 부가가치세(VAT) 10%가 포함된 금액입니다.",
      ].join("\n")
    : [
        "결제방식 : 협의",
        "계좌입금 : 카카오뱅크 3333-24-3064824, 예금주명:아이닷메타(i.meta)",
        "본 견적서에 명시된 합계 금액은 부가가치세(VAT) 10%가 포함된 금액입니다.",
      ].join("\n");

const EMPTY_CLIENT = { name: "", biz_reg_no: "", representative: "", address: "", contact_name: "", tel: "", email: "" };
const PARTNER_TYPE = "공동개발(Partnership) 문의"; // 백엔드 app/inquiries.py PARTNER_TYPE

/* 개발 문의(?inquiry=ID)에서 바로 쓰는 견적서 — 업체 등록 없이, 접수된 내용으로 고객사 · 제목을 채운다 */
const clientFromInquiry = (q) => ({
  ...EMPTY_CLIENT,
  name: q.company || q.name || "",
  contact_name: q.name || "",
  tel: q.phone || "",
  email: q.email || "",
});
const titleFromInquiry = (q) => `${q.inquiry_type === PARTNER_TYPE ? "공동개발" : q.inquiry_type || "개발"} 견적`;

// 발송 이메일 기본 본문 (수정 가능)
const emailBodyDefault = (docType) =>
  docType === "PO"
    ? `안녕하세요.
아이닷메타 선성태 입니다.

아래와 같이 발주드립니다. 확인 부탁드립니다.
감사합니다.

선성태 드림.
`
    : `안녕하세요.
아이닷메타 선성태 입니다.

요청주신 ${DOC_SHORT[docType] || "문서"}를 보내드립니다.
감사합니다.

선성태 드림.
`;

// 출력 언어 — 영문은 [영문 번역]으로 만든 번역본(검토 · 수정 가능)을 쓴다 (백엔드 app/sstoa/translate.py · pdf.py)
const LANGS = [["ko", "한국어"], ["en", "영문"], ["both", "한국어 + 영문"]];
const EN_SUPPLIER_KEYS = [["name", "상호"], ["representative", "대표자"], ["address", "주소"], ["biz_condition", "업태"], ["biz_item", "종목"]];
const EN_CLIENT_KEYS = [["name", "상호"], ["address", "주소"], ["contact_name", "담당자"]]; // 고객사는 대표자 대신 담당자만 서류에 넣는다
// 화면에서 고친 공급사 영문 표기(한국어 값 → 영문)를 기억해 다음 번역에도 그대로 쓴다
const KNOWN_KEY = "sstoa.known_en.v1";
const loadKnown = () => {
  try { return JSON.parse(localStorage.getItem(KNOWN_KEY)) || {}; } catch { return {}; }
};
const saveKnown = (m) => {
  try { localStorage.setItem(KNOWN_KEY, JSON.stringify(m)); } catch { /* 저장 안 됨 */ }
};

const won = (n) => (Number(n) || 0).toLocaleString("ko-KR");
// 입력칸 표시용: 숫자만 남겨 1,000 단위 콤마로 표시 (빈 값은 빈 문자열)
const comma = (v) => {
  const s = String(v ?? "").replace(/[^0-9]/g, "");
  return s === "" ? "" : Number(s).toLocaleString("ko-KR");
};
const today = () => new Date().toISOString().slice(0, 10);

// 접이식 카드
function Card({ title, action, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sstoa-panel sstoa-card">
      <div className="sstoa-card__head">
        <button type="button" className="sstoa-card__title" onClick={() => setOpen((o) => !o)}>
          <span className="sstoa-card__chev">{open ? "▾" : "▸"}</span>{title}
        </button>
        {action ? <div className="sstoa-card__action">{action}</div> : null}
      </div>
      {open ? <div className="sstoa-card__body">{children}</div> : null}
    </div>
  );
}

// 영문 번역본 검토 · 수정 칸
function EnglishForm({ en, enStale, busy, lang, items, docType, updEn, updEnSide, updEnItem, keep }) {
  const side = (title, key, keys, note) => (
    <div className="sstoa-subpanel">
      <div className="sstoa-subpanel__h">{title}</div>
      {keys.map(([k, label]) => (
        <div key={k} className="sstoa-field" style={{ marginTop: 8 }}>
          <label>{label}</label>
          <input className="sstoa-input" value={(en[key] || {})[k] || ""} onChange={updEnSide(key, k)} disabled={busy} />
        </div>
      ))}
      {note && <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--s-fg-mute)" }}>{note}</p>}
    </div>
  );
  return (
    <div style={{ display: "grid", gap: 14 }}>
      {enStale && (
        <div className="sstoa-error" style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <span>번역한 뒤 한국어 내용이 바뀌었습니다. [다시 번역]하거나, 영문을 직접 맞춘 뒤 '이대로 사용'을 누르세요.</span>
          <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={keep} disabled={busy}>이대로 사용</button>
        </div>
      )}
      <div className="sstoa-form-cols">
        {side(`${clientLabel(docType)} (영문)`, "client", EN_CLIENT_KEYS)}
        {side(`${supplierLabel(docType)} (영문)`, "supplier", EN_SUPPLIER_KEYS, "여기서 고친 표기(예: 대표자 이름 철자)는 기억해 두고 다음 번역에도 씁니다.")}
      </div>
      <div className="sstoa-row">
        <div className="sstoa-field"><label>{docType === "INVOICE" ? "Payment Due" : "Validity"} ({validityLabel(docType)})</label>
          <input className="sstoa-input" value={en.validity || ""} onChange={updEn("validity")} disabled={busy} /></div>
        <div className="sstoa-field"><label>{serviceLabel(docType)} (영문)</label>
          <input className="sstoa-input" value={en.service_desc || ""} onChange={updEn("service_desc")} disabled={busy} /></div>
      </div>
      <div className="sstoa-field">
        <label>{itemsLabel(docType)} — 항목 이름 (영문)</label>
        <div style={{ display: "grid", gap: 6 }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12.5, color: "var(--s-fg-mute)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.name}>{i + 1}. {it.name}</span>
              <input className="sstoa-input" value={(en.items || [])[i] || ""} placeholder={it.name} onChange={updEnItem(i)} disabled={busy} />
            </div>
          ))}
        </div>
      </div>
      <div className="sstoa-field"><label>{NOTE_LABEL[docType]} (영문)</label>
        <textarea className="sstoa-textarea" style={{ minHeight: docType === "CONTRACT" ? 160 : 96 }} value={en.note || ""} onChange={updEn("note")} disabled={busy} /></div>
      <div className="sstoa-field"><label>비고 (영문)</label>
        <textarea className="sstoa-textarea" style={{ minHeight: 60 }} value={en.remarks || ""} onChange={updEn("remarks")} disabled={busy} /></div>
      <div className="sstoa-field"><label>이메일 본문 (영문){lang === "both" ? " — 한국어 본문 아래에 이어 붙입니다" : ""}</label>
        <textarea className="sstoa-textarea" style={{ minHeight: 150 }} value={en.email_body || ""} onChange={updEn("email_body")} disabled={busy} /></div>
    </div>
  );
}

export default function DocumentCompose({ docType }) {
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [supplier, setSupplier] = useState(SUPPLIER_DEFAULT);
  const [supplierLocked, setSupplierLocked] = useState(true);
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [date, setDate] = useState(today());
  const [validity, setValidity] = useState(() => validityDefault(docType));
  const [serviceDesc, setServiceDesc] = useState("");
  const [items, setItems] = useState([{ name: "", qty: 1, unit_price: 0 }]);
  const [vatRate, setVatRate] = useState(10);
  const [note, setNote] = useState(() => noteDefault(docType));
  const [remarks, setRemarks] = useState("");
  const [emailBody, setEmailBody] = useState(() => emailBodyDefault(docType));
  const [sendTo, setSendTo] = useState("");
  const [partiesOpen, setPartiesOpen] = useState(true);   // 고객사·공급사 통합 접기
  const [lang, setLang] = useState("ko");          // 출력 언어 ko / en / both
  const [en, setEn] = useState(null);              // 영문 번역본 (검토 · 수정)
  const [enSig, setEnSig] = useState("");          // 번역할 때의 한국어 원문 — 바뀌면 다시 번역을 권한다

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [draftId, setDraftId] = useState(null);   // 임시저장 문서 id
  const [inquiry, setInquiry] = useState(null);   // 이 견적서를 쓰게 한 개발 문의 (있으면 발송 뒤 그 문의 메모에 남긴다)
  const [nextNo, setNextNo] = useState("");        // 발송 시 부여될 문서번호(미리보기)
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // 문서번호 미리보기 — 발송 시에만 실제 부여·증가. 날짜(연월)에 따라 달라짐.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/generated/next-number?doc_type=${docType}&date=${date || ""}`);
        if (alive) setNextNo(r.doc_no || "");
      } catch {
        if (alive) setNextNo("");
      }
    })();
    return () => { alive = false; };
  }, [docType, date]);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get("/companies"), api.get("/projects")]);
        setCompanies(c);
        setProjects(p);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [docType]);

  // 임시저장 문서 이어서 작성 (?draft=<id>). 새 서류는 ?project=<id> 로 관련 프로젝트를 미리 고른다.
  useEffect(() => {
    const did = params.get("draft");
    if (!did) {
      const pid = params.get("project");
      if (pid) setProjectId(pid);
      const qid = params.get("inquiry");
      if (qid) {
        (async () => {
          try {
            const q = await api.get(`/inquiries/${qid}`);
            setInquiry(q);
            setClient(clientFromInquiry(q));
            setSendTo(q.email || "");
            setServiceDesc(titleFromInquiry(q));
          } catch (err) {
            setError(err.message);
          }
        })();
      }
      return;
    }
    (async () => {
      try {
        const d = await api.get(`/generated/${did}`);
        const f = d.form_data || {};
        setDraftId(d.id);
        if (f.date) setDate(f.date);
        if (f.validity != null) setValidity(f.validity);
        if (f.service_desc != null) setServiceDesc(f.service_desc);
        if (f.email_body != null) setEmailBody(f.email_body);
        if (f.supplier) setSupplier({ ...SUPPLIER_DEFAULT, ...f.supplier });
        if (f.client) setClient({ ...EMPTY_CLIENT, ...f.client });
        if (Array.isArray(f.items) && f.items.length) setItems(f.items);
        if (f.vat_rate != null) setVatRate(f.vat_rate);
        if (f.note != null) setNote(f.note);
        if (f.remarks != null) setRemarks(f.remarks);
        if (f.lang) setLang(f.lang);
        if (f.en) setEn(f.en);
        if (f.en_sig) setEnSig(f.en_sig);
        if (f.client && f.client.email) setSendTo(f.client.email);
        if (d.company_id) {
          setCompanyId(String(d.company_id));
          try {
            const c = await api.get(`/companies/${d.company_id}`);
            setContacts(c.contacts || []);
          } catch { /* 무시 */ }
        }
        if (d.contact_id) setContactId(String(d.contact_id));
        if (d.project_id) setProjectId(String(d.project_id));
        if (f.inquiry_id) {
          try { setInquiry(await api.get(`/inquiries/${f.inquiry_id}`)); } catch { /* 문의가 지워졌으면 연결 없이 */ }
        }
      } catch (err) {
        setError(err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function pickCompany(cid) {
    setCompanyId(cid);
    setContactId("");
    setMsg("");
    if (!cid) {
      setClient(EMPTY_CLIENT);
      setContacts([]);
      setSendTo("");
      return;
    }
    try {
      const c = await api.get(`/companies/${cid}`);
      const cs = c.contacts || [];
      setContacts(cs);
      const base = {
        name: c.name || "", biz_reg_no: c.biz_reg_no || "", representative: c.representative || "",
        address: c.address || "", contact_name: "", tel: "", email: "",
      };
      const primary = cs.find((x) => x.is_primary) || cs[0];
      if (primary) {
        base.contact_name = primary.name;
        base.tel = primary.phone || "";
        base.email = primary.email || "";
        setContactId(String(primary.id));
        setSendTo(primary.email || "");
      } else {
        setSendTo("");
      }
      setClient(base);
    } catch (err) {
      setError(err.message);
    }
  }

  function pickContact(cid) {
    setContactId(cid);
    const c = contacts.find((x) => String(x.id) === String(cid));
    if (c) {
      setClient((prev) => ({ ...prev, contact_name: c.name, tel: c.phone || "", email: c.email || "" }));
      setSendTo(c.email || "");
    } else {
      setClient((prev) => ({ ...prev, contact_name: "", tel: "", email: "" }));
      setSendTo("");
    }
  }

  const updItem = (i, k) => (e) => {
    const v = e.target.value;
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };
  const updNum = (i, k, max) => (e) => {
    let v = e.target.value.replace(/[^0-9]/g, "");
    if (v !== "") v = String(Math.min(parseInt(v, 10), max));
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };
  const addItem = () => setItems((r) => [...r, { name: "", qty: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));
  const updClient = (k) => (e) => setClient((c) => ({ ...c, [k]: e.target.value }));
  const updSupplier = (k) => (e) => setSupplier((s) => ({ ...s, [k]: e.target.value }));

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const vat = Math.round((subtotal * (Number(vatRate) || 0)) / 100);
  const total = subtotal + vat;

  // 번역할 한국어 원문 — 이것이 번역 뒤에 바뀌면 영문이 낡은 것
  const filledItems = items.filter((it) => it.name.trim());
  const sourceSig = JSON.stringify({
    validity, serviceDesc, note, remarks, emailBody,
    items: filledItems.map((it) => it.name),
    client: EN_CLIENT_KEYS.map(([k]) => client[k] || ""),
    supplier: EN_SUPPLIER_KEYS.map(([k]) => supplier[k] || ""),
  });
  const wantsEn = lang !== "ko";
  const enStale = !!en && enSig !== sourceSig;
  const enReady = !wantsEn || (!!en && !enStale);

  function buildPayload() {
    return {
      doc_type: docType,
      company_id: companyId ? Number(companyId) : null,
      project_id: projectId ? Number(projectId) : null,
      contact_id: contactId ? Number(contactId) : null,
      form_data: {
        // doc_no 는 발송 시 서버에서 자동 부여 (작성 중엔 견적번호 없음)
        date,
        validity,
        service_desc: serviceDesc,
        supplier,
        client,
        items: items.filter((it) => it.name.trim()),
        note,
        remarks,
        email_body: emailBody,
        vat_rate: Number(vatRate) || 0,
        lang,
        en: en || undefined,
        en_sig: en ? enSig : undefined,
        inquiry_id: inquiry ? inquiry.id : undefined,
      },
    };
  }

  async function translate() {
    if (busy) return;
    if (en && !window.confirm("다시 번역하면 영문에서 직접 고친 내용이 새 번역으로 바뀝니다. 계속할까요?")) return;
    setBusy(true); setError(""); setMsg("");
    try {
      const known = loadKnown();
      const p = buildPayload();
      const r = await api.post("/generated/translate", { ...p, form_data: { ...p.form_data, en: undefined, en_known: known } });
      const out = r.en;
      // 기억해 둔 공급사 영문 표기는 번역 결과보다 앞선다
      const sup = { ...out.supplier };
      for (const [k] of EN_SUPPLIER_KEYS) if (known[supplier[k]]) sup[k] = known[supplier[k]];
      setEn({ ...out, supplier: sup });
      setEnSig(sourceSig);
      setMsg("영문으로 번역했습니다. 아래 '영문 버전'에서 검토하고 필요하면 고쳐 주세요.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }
  const updEn = (k) => (e) => setEn((v) => ({ ...v, [k]: e.target.value }));
  const updEnSide = (side, k) => (e) => {
    const val = e.target.value;
    setEn((v) => ({ ...v, [side]: { ...(v[side] || {}), [k]: val } }));
    if (side === "supplier" && supplier[k]) {  // 공급사(우리 회사) 표기는 기억해 둔다
      const m = loadKnown();
      if (val.trim()) m[supplier[k]] = val.trim(); else delete m[supplier[k]];
      saveKnown(m);
    }
  };
  const updEnItem = (i) => (e) => {
    const val = e.target.value;
    setEn((v) => {
      const list = [...(v.items || [])];
      list[i] = val;
      return { ...v, items: list };
    });
  };
  const mailBody = () => (lang === "en" ? en?.email_body || "" : lang === "both" ? `${emailBody}\n\n${"-".repeat(30)}\n\n${en?.email_body || ""}` : emailBody);

  // 보내기 전에 확인하는 칸은 제공 서비스와 상세 내역(항목 · 단가 · 수량)뿐 — 나머지는 비어 있어도 보낸다(PDF 는 빈 칸을 빼거나 '—').
  // 상세 내역은 항목 이름을 적은 줄이 하나 이상이고, 그 줄마다 단가 · 수량이 있어야 한다 (완전히 빈 줄은 무시)
  const usedItems = items.filter((it) => it.name.trim() || Number(it.unit_price) > 0);
  const itemsOk = usedItems.length > 0 &&
    usedItems.every((it) => it.name.trim() && Number(it.qty) > 0 && Number(it.unit_price) > 0);
  const canSend = itemsOk && enReady && serviceDesc.trim() !== "";

  async function preview() {
    if (busy) return;
    if (wantsEn && !en) { setError("영문으로 보려면 먼저 [영문 번역]을 눌러 주세요."); return; }
    setBusy(true); setError(""); setMsg("");
    try {
      const res = await fetch("/api/sstoa/generated/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("미리보기 생성에 실패했습니다.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveDraft() {
    if (busy) return;
    setBusy(true); setError(""); setMsg("");
    try {
      const res = await api.post("/generated/draft", { id: draftId, ...buildPayload() });
      setDraftId(res.id);
      setMsg("임시저장되었습니다. (견적번호는 발송 시 부여됩니다)");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (busy || !canSend) return;
    // 받는 사람이 없으면 문서번호만 써 버리고 메일은 못 보낸다 — 먼저 묻는다
    if (!sendTo.trim()) { setError(`받는 사람 이메일이 없습니다. ${clientLabel(docType)}의 담당자 이메일을 입력해 주세요.`); return; }
    setBusy(true); setError(""); setMsg("");
    try {
      const created = await api.post("/generated", buildPayload());
      await api.post(`/generated/${created.id}/send`, { to: sendTo, body: mailBody() });
      // 임시저장본이 있었다면 발송 완료됐으므로 제거
      if (draftId) {
        try { await api.del(`/generated/${draftId}`); } catch { /* 무시 */ }
      }
      // 개발 문의에서 쓴 견적서 — 그 문의의 처리 메모에 발송 기록을 남긴다
      if (inquiry) {
        try {
          const cur = await api.get(`/inquiries/${inquiry.id}`);
          const line = `[${today()}] ${DOC_SHORT[docType]} ${created.doc_no} 발송 (${sendTo})`;
          await api.post(`/inquiries/${inquiry.id}`, { admin_memo: cur.admin_memo ? `${cur.admin_memo}\n${line}` : line });
        } catch { /* 메모는 못 남겨도 발송은 끝났다 */ }
      }
      setMsg(`${sendTo} 로 ${DOC_SHORT[docType]}를 발송했습니다. (문서번호 ${created.doc_no})`);
      setDraftId(null);
      setTimeout(() => navigate(`/sstoa/${TYPE_ROUTE[docType]}`), 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        <span style={{ margin: "0 8px", color: "var(--s-fg-mute)" }}>·</span>
        <Link to={`/sstoa/${TYPE_ROUTE[docType]}`} className="sstoa-back">{DOC_SHORT[docType]} 관리</Link>
        {inquiry && (
          <>
            <span style={{ margin: "0 8px", color: "var(--s-fg-mute)" }}>·</span>
            <Link to="/sstoa/inquiries" className="sstoa-back">개발 문의</Link>
          </>
        )}
      </div>

      <div className="sstoa-topbar">
        <h1 className="sstoa-title">{TITLES[docType]}{draftId ? <span style={{ fontSize: 13, color: "var(--s-fg-mute)", fontWeight: 400, marginLeft: 10 }}>임시저장 이어서 작성</span> : null}</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginRight: 4, whiteSpace: "nowrap" }}>
            문서번호 <b style={{ color: "var(--s-fg)", fontWeight: 700 }}>{nextNo || "…"}</b>
            <span style={{ marginLeft: 4, opacity: 0.7 }}>(발송 시 부여)</span>
          </span>
          <button type="button" className="sstoa-btn" onClick={saveDraft} disabled={busy}>임시저장</button>
          <button type="button" className="sstoa-btn" onClick={preview} disabled={busy}>미리보기</button>
          <button
            type="button"
            className="sstoa-btn sstoa-btn--primary"
            onClick={send}
            disabled={busy || !canSend}
            title={canSend ? "" : !enReady ? "영문 번역을 만들고(한국어를 고쳤다면 다시 번역하거나 '이대로 사용') 나면 활성화됩니다." : `${serviceLabel(docType)}와 ${itemsLabel(docType)}(항목 · 단가 · 수량)을 입력하면 활성화됩니다.`}
          >
            {busy ? "처리 중…" : `${DOC_SHORT[docType]} 보내기`}
          </button>
        </div>
      </div>

      {inquiry && (
        <div className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 14, padding: "12px 16px", fontSize: 13.5 }}>
          개발 문의 <b>#{inquiry.id}</b> · {inquiry.company ? `${inquiry.company} · ` : ""}{inquiry.name} ({inquiry.inquiry_type})에서 작성하는 {DOC_SHORT[docType]}입니다.
          <span style={{ color: "var(--s-fg-mute)" }}> 업체 등록 없이 보낼 수 있고, 보내면 그 문의의 처리 메모에 발송 기록이 남습니다.</span>
        </div>
      )}
      {error && <div className="sstoa-error" style={{ marginBottom: 14 }}>{error}</div>}
      {msg && <div className="sstoa-error" style={{ marginBottom: 14, background: "#e7f6ec", borderColor: "#b6e0c4", color: "#1f7a3d" }}>{msg}</div>}

      <form className="sstoa-form" style={{ maxWidth: "none" }} onSubmit={(e) => e.preventDefault()}>
        <div className="sstoa-row">
          <div className="sstoa-field"><label>{docType === "QUOTE" ? "견적일" : "작성일"}</label>
            <input className="sstoa-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} /></div>
          <div className="sstoa-field"><label>{validityLabel(docType)}</label>
            <input className="sstoa-input" value={validity} onChange={(e) => setValidity(e.target.value)} disabled={busy} /></div>
          <div className="sstoa-field"><label>출력 언어</label>
            <select className="sstoa-select" value={lang} onChange={(e) => setLang(e.target.value)} disabled={busy}>
              {LANGS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select></div>
          <div className="sstoa-field"><label>관련 프로젝트 (선택)</label>
            <select className="sstoa-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={busy}>
              <option value="">연결 안 함</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
        </div>
        <p style={{ margin: "-4px 0 0", fontSize: 12, color: "var(--s-fg-mute)" }}>
          견적번호는 발송 시 자동으로 부여됩니다. 작성 중에는 임시저장으로 보관하세요.
        </p>

        {/* 고객사 · 공급사 — 하나의 접기/펴기 버튼으로 함께 제어 */}
        <div className="sstoa-panel sstoa-card">
          <div className="sstoa-card__head">
            <button type="button" className="sstoa-card__title" onClick={() => setPartiesOpen((o) => !o)}>
              <span className="sstoa-card__chev">{partiesOpen ? "▾" : "▸"}</span>{clientLabel(docType)} · {supplierLabel(docType)}
            </button>
            {partiesOpen && (
              <div className="sstoa-card__action">
                <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => setSupplierLocked((v) => !v)} disabled={busy}>
                  {supplierLocked ? `${supplierLabel(docType)} 수정` : `${supplierLabel(docType)} 완료`}
                </button>
              </div>
            )}
          </div>
          {partiesOpen && (
            <div className="sstoa-card__body">
              <div className="sstoa-form-cols">
                {/* 고객사 / 공급처 */}
                <div className="sstoa-subpanel">
                  <div className="sstoa-subpanel__h">{clientLabel(docType)}</div>
                  <div className="sstoa-row">
                    <div className="sstoa-field"><label>업체 선택</label>
                      <select className="sstoa-select" value={companyId} onChange={(e) => pickCompany(e.target.value)} disabled={busy}>
                        <option value="">직접 입력</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select></div>
                    <div className="sstoa-field"><label>담당자</label>
                      <select className="sstoa-select" value={contactId} onChange={(e) => pickContact(e.target.value)} disabled={busy || !companyId}>
                        <option value="">{companyId ? "선택 안 함" : "업체 먼저 선택"}</option>
                        {contacts.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}{ct.email ? ` (${ct.email})` : ""}</option>)}
                      </select></div>
                  </div>
                  <div className="sstoa-row" style={{ marginTop: 12 }}>
                    <div className="sstoa-field"><label>상호</label><input className="sstoa-input" value={client.name} onChange={updClient("name")} disabled={busy} /></div>
                    <div className="sstoa-field"><label>사업자번호</label><input className="sstoa-input" value={client.biz_reg_no} onChange={updClient("biz_reg_no")} disabled={busy} /></div>
                  </div>
                  <div className="sstoa-row" style={{ marginTop: 12 }}>
                    <div className="sstoa-field"><label>담당자</label><input className="sstoa-input" value={client.contact_name} onChange={updClient("contact_name")} disabled={busy} /></div>
                    <div className="sstoa-field"><label>주소</label><input className="sstoa-input" value={client.address} onChange={updClient("address")} disabled={busy} /></div>
                  </div>
                  <div className="sstoa-row" style={{ marginTop: 12 }}>
                    <div className="sstoa-field"><label>담당자 연락처</label><input className="sstoa-input" value={client.tel} onChange={updClient("tel")} disabled={busy} /></div>
                    <div className="sstoa-field"><label>담당자 이메일 <span style={{ color: "var(--s-accent)", fontWeight: 400 }}>(발송지)</span></label><input className="sstoa-input" value={client.email} onChange={(e) => { updClient("email")(e); setSendTo(e.target.value); }} disabled={busy} /></div>
                  </div>
                </div>

                {/* 공급사 / 발주처 (I.META, 고정) */}
                <div className="sstoa-subpanel">
                  <div className="sstoa-subpanel__h">{supplierLabel(docType)}</div>
                  <div className="sstoa-row">
                    <div className="sstoa-field"><label>상호</label><input className="sstoa-input" value={supplier.name} onChange={updSupplier("name")} disabled={busy || supplierLocked} /></div>
                    <div className="sstoa-field"><label>사업자번호</label><input className="sstoa-input" value={supplier.biz_reg_no} onChange={updSupplier("biz_reg_no")} disabled={busy || supplierLocked} /></div>
                  </div>
                  <div className="sstoa-row" style={{ marginTop: 12 }}>
                    <div className="sstoa-field"><label>대표자</label><input className="sstoa-input" value={supplier.representative} onChange={updSupplier("representative")} disabled={busy || supplierLocked} /></div>
                    <div className="sstoa-field"><label>주소</label><input className="sstoa-input" value={supplier.address} onChange={updSupplier("address")} disabled={busy || supplierLocked} /></div>
                  </div>
                  <div className="sstoa-row" style={{ marginTop: 12 }}>
                    <div className="sstoa-field"><label>연락처</label><input className="sstoa-input" value={supplier.tel} onChange={updSupplier("tel")} disabled={busy || supplierLocked} /></div>
                    <div className="sstoa-field"><label>이메일</label><input className="sstoa-input" value={supplier.email} onChange={updSupplier("email")} disabled={busy || supplierLocked} /></div>
                  </div>
                  <div className="sstoa-row" style={{ marginTop: 12 }}>
                    <div className="sstoa-field"><label>업태</label><input className="sstoa-input" value={supplier.biz_condition} onChange={updSupplier("biz_condition")} disabled={busy} /></div>
                    <div className="sstoa-field"><label>종목</label><input className="sstoa-input" value={supplier.biz_item} onChange={updSupplier("biz_item")} disabled={busy} /></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sstoa-field">
          <label>{serviceLabel(docType)}</label>
          <input className="sstoa-input" value={serviceDesc} onChange={(e) => setServiceDesc(e.target.value)} placeholder={docType === "PO" ? "예: 센서 보드용 부품 구매" : "예: 서지보드 1,000 대 및 케이블 견적"} disabled={busy} />
        </div>

        {/* 상세 내역 / 구매 품목 */}
        <Card
          title={itemsLabel(docType)}
          action={
            <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={addItem} disabled={busy}>+ 항목 추가</button>
          }
        >
          <table className="sstoa-table sstoa-items">
            <colgroup>
              <col style={{ width: 44 }} />
              <col />
              <col style={{ width: 120 }} />
              <col style={{ width: 78 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 40 }} />
            </colgroup>
            <thead><tr><th className="c">번호</th><th>항목</th><th>단가</th><th>수량</th><th className="r">금액</th><th></th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} style={{ cursor: "default" }}>
                  <td className="c" style={{ color: "var(--s-fg-mute)" }}>{i + 1}</td>
                  <td><input className="sstoa-input" value={it.name} onChange={updItem(i, "name")} disabled={busy} /></td>
                  <td><input className="sstoa-input" inputMode="numeric" style={{ textAlign: "right" }} value={comma(it.unit_price)} onChange={updNum(i, "unit_price", 9999999999)} disabled={busy} /></td>
                  <td><input className="sstoa-input" inputMode="numeric" style={{ textAlign: "right" }} value={comma(it.qty)} onChange={updNum(i, "qty", 9999)} disabled={busy} /></td>
                  <td className="r" style={{ whiteSpace: "nowrap" }}>{won((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</td>
                  <td className="r"><button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => removeItem(i)} disabled={busy}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 20, marginTop: 12, alignItems: "center" }}>
            <label style={{ fontSize: 12.5, color: "var(--s-fg-mute)" }}>
              부가세율 <input className="sstoa-input" style={{ width: 56, display: "inline-block", marginLeft: 6, textAlign: "right" }} inputMode="numeric" value={vatRate} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setVatRate(v === "" ? "" : String(Math.min(parseInt(v, 10), 100))); }} disabled={busy} /> %
            </label>
            <div style={{ textAlign: "right", lineHeight: 1.7 }}>
              <div style={{ color: "var(--s-fg-mute)", fontSize: 12.5 }}>공급가액 {won(subtotal)} · 부가세 {won(vat)}</div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>합계 {won(total)} 원</div>
            </div>
          </div>
        </Card>

        {/* 특약 및 안내사항 (품목과 비고 사이) */}
        <Card title={NOTE_LABEL[docType]}>
          <textarea
            className="sstoa-textarea"
            style={{ minHeight: docType === "CONTRACT" ? 160 : 96 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--s-fg-mute)" }}>한 줄에 한 항목씩 입력하면 문서에 번호로 표시됩니다.</p>
        </Card>

        {/* 이메일 본문 (발송 시 메일 내용) */}
        <Card title="이메일 본문">
          <textarea
            className="sstoa-textarea"
            style={{ minHeight: 150 }}
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            disabled={busy}
          />
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--s-fg-mute)" }}>
            {lang === "en"
              ? "영문으로만 보낼 때는 아래 '영문 버전'의 이메일 본문이 쓰입니다."
              : `${DOC_SHORT[docType]} 발송 시 이 내용이 메일 본문으로 들어가고, PDF가 첨부됩니다.${lang === "both" ? " (한국어 · 영문 PDF 둘 다)" : ""}`}
          </p>
        </Card>

        {/* 영문 버전 — 한국어로 다 쓴 뒤 번역하고, 검토 · 수정한다 */}
        {wantsEn && (
          <Card
            title="영문 버전 (English)"
            action={
              <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={translate} disabled={busy}>
                {en ? "다시 번역" : "영문 번역"}
              </button>
            }
          >
            {!en ? (
              <p style={{ margin: 0, fontSize: 13, color: "var(--s-fg-mute)" }}>
                한국어로 내용을 다 쓴 뒤 [영문 번역]을 누르세요. 번역된 영문을 여기서 검토 · 수정한 다음 미리보기 · 발송합니다.
                숫자 · 금액 · 날짜 · 연락처는 번역하지 않고 그대로 씁니다.
              </p>
            ) : (
              <EnglishForm
                en={en} enStale={enStale} busy={busy} lang={lang} items={filledItems} docType={docType}
                updEn={updEn} updEnSide={updEnSide} updEnItem={updEnItem} keep={() => setEnSig(sourceSig)}
              />
            )}
          </Card>
        )}

        {/* 비고 */}
        <Card title="비고" defaultOpen={false}>
          <textarea
            className="sstoa-textarea"
            style={{ minHeight: 70 }}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="문서 하단에 표시할 추가 비고 (선택)"
            disabled={busy}
          />
        </Card>
      </form>
    </div>
  );
}
